import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Grid, IconButton, Typography, Chip, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicBreadcrumbs from '../../components/Shared/Breadcrumb/index.tsx';
import ConfirmBox from '../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { WebhookAction } from '../../Services/_actions/Webhook/webhook.action.js';

const truncate = (str?: string, n = 20) =>
  (str ?? '').length > n ? (str ?? '').slice(0, n) + '…' : (str ?? '');

const BotAuthenticationPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const actions = WebhookAction();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [toDelete, setToDelete] = useState<BotAuthRecord | null>(null);

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    { label: 'Bot Authentication' },
  ];

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actions.getBotAuthentication?.();
      const data = Array.isArray(res?.data) ? res.data : res?.data?.results || res?.data || [];
      setList(data);
    } catch (e: any) {
      showSnackBar(e?.message || 'Failed to load records', 'error');
    } finally {
      setLoading(false);
    }
  }, [actions, showSnackBar]);

  useEffect(() => {
    fetchList();
  }, []);

  const handleDelete = (row: BotAuthRecord) => {
    setToDelete(row);
    setOpenConfirm(true);
  };

  const onConfirmDelete = async (confirmed: boolean) => {
    setOpenConfirm(false);
    if (!confirmed || !toDelete?._id) return;
    try {
      await actions.deleteBotAuthentication?.(toDelete._id);
      setList((prevList) => prevList.filter((bot) => bot._id !== toDelete._id));
      showSnackBar('Deleted successfully', 'success');
    } catch (e: any) {
      showSnackBar(e?.message || 'Delete failed', 'error');
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        p: '5px',
        pb: '15px',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Bot Authentication
        </Typography>
        {list.length <= 2 && (
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                navigate('/r/setting/bot/create');
              }}
            >
              Create
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ p: { xs: 1, sm: 2 } }}>
        {list?.map((bot: any) => (
          <Grid item xs={12} sm={6} md={3} key={bot._id}>
            <Card
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: '#ffffff',
                border: '2px solid #3b82f6',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                  borderColor: '#3b82f6',
                },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Header row: name (truncated) + icons that never get pushed off */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={bot.app_name || ''} arrow placement="top">
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{   
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {truncate(bot.app_name, 20)}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      sx={{ width: 32, height: 32 }}
                      onClick={() => navigate(`/r/setting/bot/edit/${bot._id}`, { state: { bot } })}
                      aria-label="Edit"
                    >
                      <EditIcon sx={{ fontSize: 16, color: '#64748b' }} />
                    </IconButton>

                    <IconButton
                      size="small"
                      sx={{ width: 32, height: 32 }}
                      onClick={() => handleDelete(bot)}
                      aria-label="Delete"
                    >
                      <DeleteIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                    </IconButton>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={1} sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Phone Number:</strong> {bot.channel_number || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Channel Name:</strong> {bot.channel_name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>App ID:</strong> {bot.app_id || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ pt: 1, borderTop: '1px solid #f3f4f6', mt: 'auto' }}>
                  <Chip
                    label={bot.status ? 'Active' : 'Inactive'}
                    size="small"
                    color={bot.status ? 'success' : 'error'}
                    variant="filled"
                  />
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

    {loading ? (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Typography variant="h6" color="text.secondary">
      Loading...
    </Typography>
  </Box>
) : (
  (!list || list.length === 0) && (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Bot Authentication Records Found
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={() => navigate('/r/setting/bot/create')}
      >
        Create Your First Bot
      </Button>
    </Box>
  )
)}


      <ConfirmBox
        open={openConfirm}
        onClose={onConfirmDelete}
        options={{
          title: 'Delete Bot Authentication?',
          text: 'Are you sure you want to delete this bot authentication record?',
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};

export default BotAuthenticationPage;
